import type { SurveyDraftSection } from "@/components/features/surveys/SurveySectionEditor";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  DateFormField,
  Form,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateMatterSurvey } from "@/lib/data/react-query/matters/matter-survey";
import type { MatterSurvey } from "@/lib/data/requests/matters/matter-survey";
import {
  SurveyQuestionBuilder,
  getSurveyQuestionErrors,
  type SurveyDraftQuestion,
} from "@/components/features/surveys/SurveyQuestionBuilder";
import type { MatterContactConnection } from "@/lib/types";
import type { APIError } from "@/lib/types/APIResponse";
import { SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION } from "@/lib/data/static/slideoverStatics";
import { formatContactsName } from "@/lib/utils/format";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

function defaultExpiryDate() {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function endOfLocalDay(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999);
}

const surveyDetailsSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, "Title is required.")
    .max(200, "Title must be 200 characters or less."),
  description: z.string(),
  expiresAt: z.string().refine(
    (value) => {
      const deadline = endOfLocalDay(value);
      return (
        Boolean(value) &&
        !Number.isNaN(deadline.getTime()) &&
        deadline > new Date()
      );
    },
    { message: "Choose an expiration date that is today or later." },
  ),
});

type SurveyDetailsValues = z.infer<typeof surveyDetailsSchema>;

interface MatterSurveyCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  clientConnection: MatterContactConnection;
  portalEmail?: string;
  invitedEmail?: string | null;
  onCreated: (survey: MatterSurvey) => void;
}

export function MatterSurveyCreateModal({
  open,
  onOpenChange,
  matterId,
  clientConnection,
  portalEmail,
  invitedEmail,
  onCreated,
}: MatterSurveyCreateModalProps) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const createSurvey = useCreateMatterSurvey();
  const form = useForm<SurveyDetailsValues>({
    resolver: zodResolver(surveyDetailsSchema),
    defaultValues: {
      title: "",
      description: "",
      expiresAt: defaultExpiryDate(),
    },
  });
  const { reset } = form;
  const [questions, setQuestions] = useState<SurveyDraftQuestion[]>([]);
  const [sections, setSections] = useState<SurveyDraftSection[]>([]);
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [requestError, setRequestError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const close = () => {
    if (submitting) return;
    if (form.formState.isDirty || questions.length > 0 || sections.length > 0) {
      setConfirmClose(true);
      return;
    }
    onOpenChange(false);
  };

  useEffect(() => {
    if (!open) {
      reset({ title: "", description: "", expiresAt: defaultExpiryDate() });
      setQuestions([]);
      setSections([]);
      setValidationAttempt(0);
      setRequestError("");
      setConfirmClose(false);
    }
  }, [open, reset]);

  const submit = async ({
    title,
    description,
    expiresAt,
  }: SurveyDetailsValues) => {
    setRequestError("");
    if (questions.length === 0) {
      setValidationAttempt((attempt) => attempt + 1);
      return;
    }
    const definitions = questions.map((question) => ({
      key: question.key,
      sectionId: question.sectionId,
      displayWhen: question.displayWhen,
      prompt: question.prompt.trim(),
      type: question.type,
      required: question.required,
      options: question.options.map((option) => ({
        ...option,
        label: option.label.trim(),
      })),
    }));
    if (
      sections.some((section) => !section.title.trim()) ||
      getSurveyQuestionErrors(questions, sections).some(
        (error) =>
          error.prompt ||
          error.missingSection ||
          error.missingOptions ||
          error.options.length > 0,
      )
    ) {
      setValidationAttempt((attempt) => attempt + 1);
      return;
    }
    setValidationAttempt(0);
    setSubmitting(true);
    try {
      const response = await createSurvey.mutateAsync({
        matterId,
        title: title.trim(),
        description: description.trim() || null,
        recipientConnectionId: clientConnection.id,
        expiresAt: endOfLocalDay(expiresAt).toISOString(),
        questions: definitions,
        sections,
      });
      onOpenChange(false);
      onCreated(response.data);
    } catch (cause) {
      setRequestError(
        isAxiosError<APIError>(cause)
          ? (cause.response?.data.meta.userMsg ??
              "The survey could not be created. Please try again.")
          : "The survey could not be created. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) close();
          }}
          title="Create client survey"
          description="Prepare the questions before creating the survey."
          size="2xl"
          bodyContained
          footer={
            <div className="flex w-full justify-end gap-2">
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="create-matter-survey"
                disabled={submitting}
              >
                {submitting ? "Creating..." : "Create survey"}
              </Button>
            </div>
          }
        >
          <div
            ref={setScrollElement}
            className="min-h-0 flex-1 overflow-y-auto px-6 py-5"
          >
            <form
              id="create-matter-survey"
              className="space-y-5"
              noValidate
              onSubmit={form.handleSubmit((values) => void submit(values))}
            >
              <div className="text-muted-foreground space-y-1 text-sm">
                <p>
                  Recipient:{" "}
                  <span className="text-foreground font-medium">
                    {formatContactsName(clientConnection.contact) || "Client"}
                  </span>
                </p>
                {portalEmail ? (
                  <p>
                    Portal email:{" "}
                    <span className="text-foreground">{portalEmail}</span>
                  </p>
                ) : invitedEmail ? (
                  <p>
                    Invitation email:{" "}
                    <span className="text-foreground">{invitedEmail}</span>
                  </p>
                ) : null}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextFormField
                  register={form.register("title")}
                  label="Title *"
                  maxLength={200}
                />
                <DateFormField
                  name="expiresAt"
                  register={form.register("expiresAt")}
                  label="Expires *"
                  minDate={new Date()}
                  clearable={false}
                />
              </div>
              <TextAreaFormField
                register={form.register("description")}
                label="Description"
              />
              <SurveyQuestionBuilder
                scrollElement={scrollElement}
                questions={questions}
                sections={sections}
                onSectionsChange={setSections}
                onChange={setQuestions}
                validationAttempt={validationAttempt}
              />
              {requestError ? (
                <p role="alert" className="text-destructive text-sm">
                  {requestError}
                </p>
              ) : null}
            </form>
          </div>
        </AppModal>
      </Form>
      <ActionConfirmationModal
        open={confirmClose}
        setOpen={setConfirmClose}
        title="Unsaved changes"
        description={SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION}
        buttonText="Discard changes"
        afterActionFn={() => {
          onOpenChange(false);
        }}
      />
    </>
  );
}
